import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Remove,
  CheckCircle,
  Cancel,
  ExpandMore,
  Business,
  AttachMoney,
  Group,
  LocationOn,
  Launch,
  Lightbulb,
  Warning,
  Star,
} from '@mui/icons-material';
import PublicNavBar from './PublicNavBar';

const CompetitorResearch = () => {
  const theme = useTheme();

  const competitors = [
    {
      name: 'Spot AI',
      website: 'https://www.spot.ai/',
      logo: '🎥',
      tagline: 'AI Video Intelligence Platform',
      headquarters: 'San Francisco, CA',
      founded: '2018',
      employees: '100-250',
      funding: {
        total: '$66M+',
        lastRound: 'Series B (2022)',
        valuation: '$200-300M (est.)',
        investors: 'Redpoint Ventures, Scale Venture Partners, Bessemer',
        runway: '18-24 months (est.)',
      },
      targetMarket: 'Enterprise manufacturing, warehousing, multi-location retail, car washes, healthcare',
      pricing: 'Enterprise SaaS - Custom pricing, likely $500-2000/mo per location',
      keyFeatures: [
        'AI Security Guard (live deterrence)',
        'AI Operations Assistant',
        'Cloud Dashboard with multi-location view',
        'Intelligent Video Recorder (IVR) with edge AI',
        'IP Camera hardware at no cost',
        'Natural language video search',
      ],
      strengths: [
        'Strong enterprise focus with proven ROI stories',
        '2X more video ingested than YouTube (per their claims)',
        'Full-stack solution (hardware + software)',
        'Live security deterrence with AI agents',
        'Established customer base (1000+ customers)',
      ],
      weaknesses: [
        'Enterprise pricing excludes SMB/consumer market',
        'Requires their hardware ecosystem',
        'No offline/edge-first privacy focus',
        'Centralized cloud dependency',
      ],
      gapsVsEyeSense: [
        { gap: 'No adaptive ML fine-tuning', advantage: 'eyesense' },
        { gap: 'Cloud-dependent (no offline mode)', advantage: 'eyesense' },
        { gap: 'Enterprise-only pricing', advantage: 'eyesense' },
        { gap: 'No wildlife/garden use cases', advantage: 'eyesense' },
        { gap: 'More mature enterprise features', advantage: 'competitor' },
        { gap: 'Larger sales/support team', advantage: 'competitor' },
      ],
    },
    {
      name: 'CamThink',
      website: 'https://www.camthink.ai/',
      logo: '📷',
      tagline: 'Edge AI Hardware for Developers',
      headquarters: 'China (Milesight subsidiary)',
      founded: '2024',
      employees: '10-50',
      funding: {
        total: 'Self-funded (Milesight backing)',
        lastRound: 'N/A - Corporate spinoff',
        valuation: 'Unknown',
        investors: 'Milesight (parent company)',
        runway: 'N/A - Corporate backed',
      },
      targetMarket: 'Developers, hobbyists, embedded systems engineers, IoT builders',
      pricing: 'Hardware sales: $30-500 per device (NE101: $30, NG4500: ~$400+)',
      keyFeatures: [
        'NeoEyes NE101 (ESP32-S3 camera, $30)',
        'NeoEyes NE301 (STM32N6 Edge AI camera)',
        'NeoEdge NG4500 (Jetson Orin AI box)',
        'Open-source friendly',
        'Developer documentation & GitHub',
        'YOLO/Ultralytics integration',
      ],
      strengths: [
        'Very affordable hardware',
        'Developer-focused with open tools',
        'Milesight manufacturing & supply chain',
        'ESP32 ecosystem compatibility',
        'Good for custom/DIY projects',
      ],
      weaknesses: [
        'Hardware-only - no cloud platform',
        'No turnkey solution for end users',
        'Limited software ecosystem',
        'Early stage brand recognition',
        'Requires technical expertise',
      ],
      gapsVsEyeSense: [
        { gap: 'No complete software platform', advantage: 'eyesense' },
        { gap: 'No automated detection pipelines', advantage: 'eyesense' },
        { gap: 'Requires developer skills', advantage: 'eyesense' },
        { gap: 'Cheaper hardware entry point', advantage: 'competitor' },
        { gap: 'More hardware variety', advantage: 'competitor' },
      ],
    },
    {
      name: 'EarthCam',
      website: 'https://www.earthcam.com/',
      logo: '🌍',
      tagline: 'Construction Camera Solutions',
      headquarters: 'Upper Saddle River, NJ',
      founded: '1996',
      employees: '100-200',
      funding: {
        total: 'Bootstrapped/Private',
        lastRound: 'N/A',
        valuation: '$50-100M (est.)',
        investors: 'Private (founder-owned)',
        runway: 'Profitable (est.)',
      },
      targetMarket: 'Construction, infrastructure, tourism, public works',
      pricing: 'Camera rental + monitoring: $300-1500/mo per camera',
      keyFeatures: [
        'Professional construction webcams',
        'Time-lapse documentation',
        'Project management integration',
        'AI-powered analytics',
        'Live streaming capabilities',
        '24/7 support & monitoring',
      ],
      strengths: [
        '25+ years in market (established brand)',
        'Specialized for construction vertical',
        'Professional-grade hardware',
        'Time-lapse and documentation focus',
        'Trusted by major construction firms',
      ],
      weaknesses: [
        'Construction-only focus',
        'Expensive hardware rental model',
        'Legacy technology stack',
        'No consumer/SMB offering',
        'Limited AI capabilities vs modern competitors',
      ],
      gapsVsEyeSense: [
        { gap: 'Limited AI/ML capabilities', advantage: 'eyesense' },
        { gap: 'Single vertical focus', advantage: 'eyesense' },
        { gap: 'Expensive rental model', advantage: 'eyesense' },
        { gap: 'Established construction relationships', advantage: 'competitor' },
        { gap: 'Time-lapse specialization', advantage: 'competitor' },
      ],
    },
    {
      name: 'Securade.ai',
      website: 'https://securade.ai/',
      logo: '🦺',
      tagline: 'Workplace Safety AI',
      headquarters: 'Singapore',
      founded: '2020',
      employees: '20-50',
      funding: {
        total: '$5-10M (est.)',
        lastRound: 'Seed/Series A',
        valuation: '$20-40M (est.)',
        investors: 'Asian VCs, Angels',
        runway: '12-18 months (est.)',
      },
      targetMarket: 'Manufacturing, construction, industrial workplaces',
      pricing: 'SaaS: ~$200-500/mo per site (est.)',
      keyFeatures: [
        'PPE compliance detection',
        'Safety zone monitoring',
        'Incident detection & alerts',
        'Works with existing cameras',
        'Real-time dashboard',
        'Compliance reporting',
      ],
      strengths: [
        'Specialized workplace safety focus',
        'Camera-agnostic (works with existing)',
        'Strong compliance/regulatory angle',
        'Growing market for workplace safety',
      ],
      weaknesses: [
        'Narrow use case (safety only)',
        'Smaller company, limited resources',
        'Less brand recognition',
        'Geographic focus (Asia)',
      ],
      gapsVsEyeSense: [
        { gap: 'Limited to safety use cases', advantage: 'eyesense' },
        { gap: 'No edge processing option', advantage: 'eyesense' },
        { gap: 'No adaptive learning', advantage: 'eyesense' },
        { gap: 'Safety compliance expertise', advantage: 'competitor' },
      ],
    },
    {
      name: 'TPC Security',
      website: 'https://security.tpcsecurity.com/',
      logo: '🔒',
      tagline: 'Traditional Security Systems',
      headquarters: 'USA',
      founded: '2000s',
      employees: '50-100',
      funding: {
        total: 'Unknown - Traditional business',
        lastRound: 'N/A',
        valuation: 'Unknown',
        investors: 'Private',
        runway: 'Operating business',
      },
      targetMarket: 'Commercial security, access control, traditional surveillance',
      pricing: 'Installation + monthly monitoring fees',
      keyFeatures: [
        'Traditional CCTV installation',
        'Access control systems',
        'Alarm monitoring',
        '24/7 monitoring centers',
        'On-site service',
      ],
      strengths: [
        'Full-service security provider',
        'Human monitoring infrastructure',
        'Physical installation services',
        'Established customer relationships',
      ],
      weaknesses: [
        'Legacy technology approach',
        'Labor-intensive monitoring model',
        'Limited AI capabilities',
        'High operational costs',
      ],
      gapsVsEyeSense: [
        { gap: 'No modern AI/ML capabilities', advantage: 'eyesense' },
        { gap: 'High labor costs', advantage: 'eyesense' },
        { gap: 'No edge processing', advantage: 'eyesense' },
        { gap: 'Physical service network', advantage: 'competitor' },
        { gap: 'Existing customer base', advantage: 'competitor' },
      ],
    },
    {
      name: 'LVT (LiveView Technologies)',
      website: 'https://www.lvt.com/',
      logo: '🗼',
      tagline: 'Mobile Surveillance Units',
      headquarters: 'Orem, UT',
      founded: '2017',
      employees: '200-500',
      funding: {
        total: '$200M+',
        lastRound: 'Series D (2023)',
        valuation: '$500M-1B (est.)',
        investors: 'Goldman Sachs, Bain Capital, etc.',
        runway: '24+ months',
      },
      targetMarket: 'Retail parking lots, construction sites, events, utilities',
      pricing: 'Unit rental: $1,000-3,000/mo per trailer',
      keyFeatures: [
        'Solar-powered mobile surveillance trailers',
        'LTE/cellular connectivity',
        'License plate recognition',
        'Two-way audio communication',
        'Rapid deployment (no wiring)',
        'AI-powered analytics',
        'Live monitoring option',
      ],
      strengths: [
        'Mobile/temporary deployment',
        'Solar-powered (no infrastructure needed)',
        'Strong retail security vertical',
        'Well-funded with fast growth',
        'Flexible placement options',
      ],
      weaknesses: [
        'High monthly costs',
        'Physical trailer footprint',
        'Limited to outdoor use cases',
        'Not for permanent installations',
        'No consumer market presence',
      ],
      gapsVsEyeSense: [
        { gap: 'Not for home/residential use', advantage: 'eyesense' },
        { gap: 'Much higher price point', advantage: 'eyesense' },
        { gap: 'No privacy-first approach', advantage: 'eyesense' },
        { gap: 'No adaptive ML', advantage: 'eyesense' },
        { gap: 'Rapid temporary deployment', advantage: 'competitor' },
        { gap: 'Solar-powered mobility', advantage: 'competitor' },
      ],
    },
    {
      name: 'Veesion',
      website: 'https://veesion.io/',
      logo: '🛒',
      tagline: 'AI Retail Theft Detection',
      headquarters: 'Paris, France',
      founded: '2019',
      employees: '50-100',
      funding: {
        total: '€30M+',
        lastRound: 'Series B (2024)',
        valuation: '€100-150M (est.)',
        investors: 'Eurazeo, European VCs',
        runway: '18-24 months',
      },
      targetMarket: 'Retail stores, supermarkets, pharmacies',
      pricing: 'SaaS: €200-800/mo per store (est.)',
      keyFeatures: [
        'Gesture recognition for theft detection',
        'Real-time suspicious behavior alerts',
        'Works with existing cameras',
        'Staff notification system',
        'Analytics dashboard',
        'GDPR compliant (no facial recognition)',
      ],
      strengths: [
        'Specialized theft detection AI',
        'Strong European market presence',
        'GDPR-first privacy approach',
        'Works with existing infrastructure',
        'Clear ROI for retail',
      ],
      weaknesses: [
        'Single use case (retail theft)',
        'Primarily European focus',
        'No edge processing option',
        'Requires constant connectivity',
      ],
      gapsVsEyeSense: [
        { gap: 'Single vertical (retail only)', advantage: 'eyesense' },
        { gap: 'Cloud-dependent', advantage: 'eyesense' },
        { gap: 'No edge/offline option', advantage: 'eyesense' },
        { gap: 'No learning/fine-tuning', advantage: 'eyesense' },
        { gap: 'Retail theft expertise', advantage: 'competitor' },
        { gap: 'European market presence', advantage: 'competitor' },
      ],
    },
  ];

  const featureComparison = [
    { feature: 'Edge AI Processing', eyesense: true, spotai: true, camthink: true, earthcam: false, securade: false, tpc: false, lvt: true, veesion: false },
    { feature: 'Offline Operation', eyesense: true, spotai: false, camthink: true, earthcam: false, securade: false, tpc: false, lvt: false, veesion: false },
    { feature: 'Adaptive ML Fine-tuning', eyesense: true, spotai: false, camthink: false, earthcam: false, securade: false, tpc: false, lvt: false, veesion: false },
    { feature: 'LLM Validation', eyesense: true, spotai: false, camthink: false, earthcam: false, securade: false, tpc: false, lvt: false, veesion: false },
    { feature: 'Consumer/SMB Pricing', eyesense: true, spotai: false, camthink: true, earthcam: false, securade: false, tpc: false, lvt: false, veesion: false },
    { feature: 'Wildlife Detection', eyesense: true, spotai: false, camthink: false, earthcam: false, securade: false, tpc: false, lvt: false, veesion: false },
    { feature: 'IoT Integration (Sprinklers)', eyesense: true, spotai: false, camthink: false, earthcam: false, securade: false, tpc: false, lvt: false, veesion: false },
    { feature: 'Works with Existing Cameras', eyesense: true, spotai: false, camthink: true, earthcam: false, securade: true, tpc: true, lvt: false, veesion: true },
    { feature: 'Zero Trust VPN Security', eyesense: true, spotai: false, camthink: false, earthcam: false, securade: false, tpc: false, lvt: false, veesion: false },
    { feature: 'Enterprise Support', eyesense: false, spotai: true, camthink: false, earthcam: true, securade: true, tpc: true, lvt: true, veesion: true },
    { feature: 'Multi-location Dashboard', eyesense: true, spotai: true, camthink: false, earthcam: true, securade: true, tpc: true, lvt: true, veesion: true },
    { feature: 'Mobile App', eyesense: false, spotai: true, camthink: false, earthcam: true, securade: true, tpc: true, lvt: true, veesion: true },
  ];

  const marketGaps = [
    {
      gap: 'Learning Camera Systems',
      description: 'No competitor offers cameras that improve detection accuracy over time through overnight fine-tuning. EyeSense.ai is the only "learning thermostat for cameras".',
      opportunity: 'high',
    },
    {
      gap: 'Privacy-First Edge AI',
      description: 'Most competitors are cloud-dependent. EyeSense offers true offline operation with data staying on-premise.',
      opportunity: 'high',
    },
    {
      gap: 'Consumer/Prosumer Market',
      description: 'Enterprise players ignore the $15/mo home user. CamThink requires technical skills. EyeSense bridges this gap.',
      opportunity: 'high',
    },
    {
      gap: 'Wildlife & Garden Automation',
      description: 'No competitor specifically targets deer deterrence, garden protection, or wildlife automation.',
      opportunity: 'medium',
    },
    {
      gap: 'Multi-Hardware Support',
      description: 'Most competitors lock you into their hardware. EyeSense runs on Raspberry Pi, Jetson, Mac, and Windows.',
      opportunity: 'medium',
    },
    {
      gap: 'LLM-Validated Detection',
      description: 'Two-stage detection (YOLO + LLM validation) is unique to EyeSense.ai.',
      opportunity: 'high',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicNavBar />
      
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha('#1a1a2e', 0.95)} 0%, ${alpha('#16213e', 0.95)} 100%)`,
          py: 8,
          pt: 12,
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>INTERNAL DOCUMENT</strong> — This page is not linked from the public site. For strategic planning only.
          </Alert>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            🔍 Competitor Research
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9 }}>
            Video AI & Security Camera Market Analysis
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
            Last updated: January 2026 | Status: Research in Progress
          </Typography>
        </Container>
      </Box>

      {/* Market Overview */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
          Market Overview
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                $30B+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Video Surveillance Market (2025)
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.success.main, 0.05) }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                15%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                YoY Growth Rate (AI Video)
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.info.main, 0.05) }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                Edge AI
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fastest Growing Segment
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                Privacy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                #1 Customer Concern
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Feature Comparison Table */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          Feature Comparison Matrix
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 6, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 200 }}>Feature</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', bgcolor: alpha('#fff', 0.1) }}>EyeSense.ai</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Spot AI</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>CamThink</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>EarthCam</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Securade</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>TPC</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>LVT</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Veesion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {featureComparison.map((row, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.grey[100], 0.5) } }}>
                  <TableCell sx={{ fontWeight: 'medium' }}>{row.feature}</TableCell>
                  <TableCell align="center" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    {row.eyesense ? <CheckCircle color="success" /> : <Cancel color="disabled" />}
                  </TableCell>
                  <TableCell align="center">{row.spotai ? <CheckCircle color="success" /> : <Cancel color="disabled" />}</TableCell>
                  <TableCell align="center">{row.camthink ? <CheckCircle color="success" /> : <Cancel color="disabled" />}</TableCell>
                  <TableCell align="center">{row.earthcam ? <CheckCircle color="success" /> : <Cancel color="disabled" />}</TableCell>
                  <TableCell align="center">{row.securade ? <CheckCircle color="success" /> : <Cancel color="disabled" />}</TableCell>
                  <TableCell align="center">{row.tpc ? <CheckCircle color="success" /> : <Cancel color="disabled" />}</TableCell>
                  <TableCell align="center">{row.lvt ? <CheckCircle color="success" /> : <Cancel color="disabled" />}</TableCell>
                  <TableCell align="center">{row.veesion ? <CheckCircle color="success" /> : <Cancel color="disabled" />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Market Gaps & Opportunities */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          <Lightbulb sx={{ mr: 1, verticalAlign: 'middle' }} />
          Market Gaps & EyeSense Opportunities
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {marketGaps.map((item, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper sx={{ p: 3, height: '100%', borderLeft: `4px solid ${item.opportunity === 'high' ? theme.palette.success.main : theme.palette.warning.main}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {item.gap}
                  </Typography>
                  <Chip 
                    label={item.opportunity.toUpperCase()} 
                    size="small" 
                    color={item.opportunity === 'high' ? 'success' : 'warning'} 
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Individual Competitor Cards */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
          Detailed Competitor Profiles
        </Typography>
        {competitors.map((competitor, index) => (
          <Accordion key={index} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                <Typography variant="h5" sx={{ mr: 2 }}>{competitor.logo}</Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {competitor.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {competitor.tagline}
                  </Typography>
                </Box>
                <Chip label={competitor.funding.total} size="small" sx={{ mr: 1 }} />
                <Link href={competitor.website} target="_blank" onClick={(e) => e.stopPropagation()}>
                  <Launch fontSize="small" />
                </Link>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Company Info */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Company Info
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">{competitor.headquarters}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Group fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">{competitor.employees} employees</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Business fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">Founded: {competitor.founded}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Funding
                    </Typography>
                    <Typography variant="body2">Total: <strong>{competitor.funding.total}</strong></Typography>
                    <Typography variant="body2">Last Round: {competitor.funding.lastRound}</Typography>
                    <Typography variant="body2">Valuation: {competitor.funding.valuation}</Typography>
                    <Typography variant="body2">Runway: {competitor.funding.runway}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Investors: {competitor.funding.investors}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Product Info */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Product & Pricing
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Target:</strong> {competitor.targetMarket}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Pricing:</strong> {competitor.pricing}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Key Features
                    </Typography>
                    <List dense>
                      {competitor.keyFeatures.map((feature, i) => (
                        <ListItem key={i} sx={{ py: 0.25, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckCircle fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>

                {/* SWOT */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.success.main }}>
                      <TrendingUp fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                      Strengths
                    </Typography>
                    <List dense>
                      {competitor.strengths.map((item, i) => (
                        <ListItem key={i} sx={{ py: 0.25, px: 0 }}>
                          <ListItemText primary={`• ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.error.main }}>
                      <TrendingDown fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                      Weaknesses
                    </Typography>
                    <List dense>
                      {competitor.weaknesses.map((item, i) => (
                        <ListItem key={i} sx={{ py: 0.25, px: 0 }}>
                          <ListItemText primary={`• ${item}`} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>

                {/* Gap Analysis */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                      <Star sx={{ mr: 0.5, verticalAlign: 'middle', color: theme.palette.warning.main }} />
                      Gap Analysis vs EyeSense.ai
                    </Typography>
                    <Grid container spacing={2}>
                      {competitor.gapsVsEyeSense.map((gap, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {gap.advantage === 'eyesense' ? (
                              <Chip 
                                label="✓ EyeSense Advantage" 
                                size="small" 
                                color="success" 
                                sx={{ mr: 1, fontSize: '0.7rem' }} 
                              />
                            ) : (
                              <Chip 
                                label="⚠ Competitor Lead" 
                                size="small" 
                                color="warning" 
                                sx={{ mr: 1, fontSize: '0.7rem' }} 
                              />
                            )}
                            <Typography variant="body2">{gap.gap}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Strategic Recommendations */}
        <Box sx={{ mt: 6, p: 4, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            📋 Strategic Recommendations
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main, mb: 2 }}>
                Immediate Opportunities
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText 
                    primary="Double down on adaptive ML fine-tuning messaging" 
                    secondary="Unique differentiator - no competitor offers this"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText 
                    primary="Target the 'prosumer' gap" 
                    secondary="$15-50/mo range underserved by enterprise players"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText 
                    primary="Lead with privacy messaging" 
                    secondary="Offline-first operation is increasingly valuable"
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main, mb: 2 }}>
                Areas to Develop
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><Warning color="warning" /></ListItemIcon>
                  <ListItemText 
                    primary="Mobile app experience" 
                    secondary="All major competitors have mobile apps"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Warning color="warning" /></ListItemIcon>
                  <ListItemText 
                    primary="Enterprise-grade support" 
                    secondary="Needed for larger deals, but maintain SMB focus"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Warning color="warning" /></ListItemIcon>
                  <ListItemText 
                    primary="Case studies & social proof" 
                    secondary="Need testimonials and success stories"
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Box>

        {/* Research Notes */}
        <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.grey[500], 0.1), borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            📝 Research Notes & TODOs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Funding data is estimated from public sources - verify via Crunchbase/PitchBook<br />
            • Pricing is estimated from industry analysis - consider mystery shopping<br />
            • Add: Rhombus Systems, Verkada, Axis Communications to analysis<br />
            • Monitor: Spot AI blog for product announcements<br />
            • Track: Veesion US expansion timeline<br />
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CompetitorResearch;

